import React from "react";

/* All decorations use currentColor; set text color to var(--page-accent) on the wrapper.
   They are decorative only: aria-hidden + pointer-events-none. */

export const RadarRings = ({ className = "", radii = [490, 400, 310, 215, 130] }) => (
  <svg viewBox="0 0 1000 1000" aria-hidden="true" className={`pointer-events-none ${className}`}>
    {radii.map((r, i) => (
      <circle key={r} cx="500" cy="500" r={r} fill="none" stroke="currentColor" strokeOpacity={0.05 + i * 0.012} strokeWidth="1" />
    ))}
    <line x1="500" y1="6" x2="500" y2="40" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" />
    <line x1="994" y1="500" x2="960" y2="500" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" />
    <line x1="500" y1="994" x2="500" y2="960" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" />
    <line x1="6" y1="500" x2="40" y2="500" stroke="currentColor" strokeOpacity="0.35" strokeWidth="1.5" />
  </svg>
);

export const DashRing = ({ className = "", style }) => (
  <svg viewBox="0 0 300 300" aria-hidden="true" style={style} className={`pointer-events-none ${className}`}>
    <circle cx="150" cy="150" r="146" fill="none" stroke="currentColor" strokeOpacity="0.16" strokeWidth="1" />
    <circle cx="150" cy="150" r="146" fill="none" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" strokeDasharray="2 12" />
  </svg>
);

export const HexOutline = ({ className = "", fill = 0.05, stroke = 0.2 }) => (
  <svg viewBox="0 0 268 308" aria-hidden="true" className={`pointer-events-none ${className}`}>
    <polygon points="134,2 266,70 266,238 134,306 2,238 2,70" fill="currentColor" fillOpacity={fill} stroke="currentColor" strokeOpacity={stroke} strokeWidth="1.5" />
  </svg>
);

export const TriangleOutline = ({ className = "" }) => (
  <svg viewBox="0 0 220 200" aria-hidden="true" className={`pointer-events-none ${className}`}>
    <polygon points="110,4 216,196 4,196" fill="none" stroke="currentColor" strokeOpacity="0.16" strokeWidth="1.5" />
    <polygon points="110,34 188,180 32,180" fill="none" stroke="currentColor" strokeOpacity="0.09" strokeWidth="1" />
  </svg>
);

export const CrossTicks = ({ className = "" }) => (
  <svg viewBox="0 0 40 40" aria-hidden="true" className={`pointer-events-none ${className}`}>
    <line x1="20" y1="4" x2="20" y2="36" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" />
    <line x1="4" y1="20" x2="36" y2="20" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1" />
  </svg>
);

/* Circular badge with text running around the ring + a center stat. */
export const CircleBadge = ({ value, label, ring = "EMAILINK · STUDIO · AGENCY · APPS · LAB · ", className = "" }) => {
  const uid = React.useId().replace(/[:]/g, "");
  const pathId = `badge-${uid}`;
  return (
    <svg viewBox="0 0 280 280" aria-hidden="true" className={className}>
      <circle cx="140" cy="140" r="136" fill="none" stroke="currentColor" strokeOpacity="0.14" strokeWidth="1" />
      <circle cx="140" cy="140" r="136" fill="none" stroke="currentColor" strokeOpacity="0.24" strokeWidth="1" strokeDasharray="3 10" />
      <circle cx="140" cy="140" r="96" fill="currentColor" fillOpacity="0.05" />
      <circle cx="140" cy="140" r="96" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="1" />
      <defs>
        <path id={pathId} d="M140,140 m-118,0 a118,118 0 1,1 236,0 a118,118 0 1,1 -236,0" />
      </defs>
      <text fill="currentColor" fillOpacity="0.6" fontSize="11.5" fontFamily="'IBM Plex Mono', monospace" letterSpacing="3.4">
        <textPath href={`#${pathId}`}>{ring}</textPath>
      </text>
      <line x1="140" y1="4" x2="140" y2="20" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.5" />
      <line x1="276" y1="140" x2="260" y2="140" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.5" />
      <line x1="140" y1="276" x2="140" y2="260" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.5" />
      <line x1="4" y1="140" x2="20" y2="140" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.5" />
      <text x="140" y="130" textAnchor="middle" fill="var(--text-0)" style={{ fill: "var(--text-0)" }} fontFamily="'Bebas Neue', sans-serif" fontSize="50" letterSpacing="1">{value}</text>
      <text x="140" y="152" textAnchor="middle" fill="currentColor" fillOpacity="0.7" fontFamily="'IBM Plex Mono', monospace" fontSize="9.5" letterSpacing="3">{label}</text>
    </svg>
  );
};

/* Upward triangle with a stat inside (agency hero). */
export const StatTriangle = ({ value, l1, l2, className = "" }) => (
  <svg viewBox="0 0 172 152" aria-hidden="true" className={className}>
    <polygon points="86,6 168,146 4,146" fill="currentColor" fillOpacity="0.07" stroke="currentColor" strokeOpacity="0.32" strokeWidth="1.5" />
    <polygon points="86,28 148,134 24,134" fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="1" />
    <text x="86" y="100" textAnchor="middle" fontFamily="'Bebas Neue', sans-serif" fontSize="34" fill="var(--text-0)" style={{ fill: "var(--text-0)" }} letterSpacing="1">{value}</text>
    <text x="86" y="117" textAnchor="middle" fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill="currentColor" fillOpacity="0.7" letterSpacing="3">{l1}</text>
    <text x="86" y="130" textAnchor="middle" fontFamily="'IBM Plex Mono', monospace" fontSize="8" fill="currentColor" fillOpacity="0.7" letterSpacing="3">{l2}</text>
  </svg>
);
